/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { OverviewProblem } from "@/api/overview";
import type { HubDevice } from "@/api/homeassistant";
import { buzz, HAPTIC } from "./haptics";

export type NoticeKind = "crit" | "warn" | "ok" | "info";

export interface Notice {
  id: number;
  kind: NoticeKind;
  /** short label above the text ("Critical failure", "Recovered", …) */
  label: string;
  name: string;
  text?: string;
  /** a sticky notice stays until dismissed or the cause goes away */
  sticky?: boolean;
  /** problem key it belongs to, so a recovery can clear it */
  key?: string;
}

const SOUND_KEY = "nyrvex-hub-sound";
// A problem has to last this long before it is announced. Monitors blip for a
// minute or two all the time (a router's YouTube probe, a service check), and
// announcing each blip made the display beep constantly.
const SETTLE_MS: Record<"crit" | "warn", number> = { crit: 90_000, warn: 300_000 };
// Never two tones closer together than this.
const MIN_TONE_GAP_MS = 30_000;
const LIFETIME: Record<NoticeKind, number> = { crit: 0, warn: 25_000, ok: 9_000, info: 12_000 };

function readSound(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

/** Small synthesized alerts: no audio files to ship, and they respect the device volume. */
class Beeper {
  private ctx: AudioContext | null = null;

  /** Browsers only let audio start after a tap; call this from a gesture. */
  unlock(): boolean {
    try {
      if (!this.ctx) this.ctx = new AudioContext();
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return this.ctx.state !== "closed";
    } catch {
      return false;
    }
  }

  ready(): boolean {
    return this.ctx?.state === "running";
  }

  private tone(freq: number, at: number, dur: number, gain: number, type: OscillatorType = "sine") {
    const ctx = this.ctx;
    if (!ctx) return;
    const t0 = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  }

  play(kind: NoticeKind) {
    if (!this.ready()) return;
    switch (kind) {
      case "crit": // urgent: three rising pairs
        for (let i = 0; i < 3; i++) {
          this.tone(880, i * 0.5, 0.18, 0.35, "square");
          this.tone(660, i * 0.5 + 0.2, 0.22, 0.35, "square");
        }
        break;
      case "warn": // two soft beeps
        this.tone(660, 0, 0.16, 0.25, "triangle");
        this.tone(660, 0.24, 0.16, 0.25, "triangle");
        break;
      case "ok": // a rising chime
        this.tone(784, 0, 0.18, 0.22);
        this.tone(1047, 0.14, 0.32, 0.22);
        break;
      default: // info: one friendly note
        this.tone(988, 0, 0.3, 0.2);
    }
  }
}

const COUNT_KIND = (p: OverviewProblem): NoticeKind => (p.severity === "crit" ? "crit" : "warn");

/**
 * Turns changes in the fleet and in the vacuum into on-screen notices and,
 * when sound is on, a matching tone. The first data load is the baseline and
 * never notifies.
 */
export function useHubNotices(problems: OverviewProblem[] | undefined, devices: HubDevice[], quiet: boolean, muted: boolean) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [sound, setSoundState] = useState<boolean>(readSound);
  const [audioReady, setAudioReady] = useState(false);
  const beeper = useRef(new Beeper());
  const seq = useRef(0);
  // problem key -> when it first showed up, and whether we have told the user
  const seen = useRef(new Map<string, { since: number; announced: boolean; p: OverviewProblem }>());
  const baselined = useRef(false);
  const problemsRef = useRef<OverviewProblem[] | undefined>(problems);
  const lastTone = useRef(0);
  const prevVacuum = useRef<string | null>(null);
  const timers = useRef(new Map<number, number>());
  const soundRef = useRef(sound);
  const quietRef = useRef(quiet);
  const mutedRef = useRef(muted);
  useEffect(() => {
    soundRef.current = sound;
    quietRef.current = quiet;
    mutedRef.current = muted;
  }, [sound, quiet, muted]);

  const dismiss = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setNotices((n) => n.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (n: Omit<Notice, "id">, silent = false) => {
      const id = ++seq.current;
      setNotices((cur) => [...cur.filter((x) => !(n.key && x.key === n.key)), { ...n, id }].slice(-4));
      const life = n.sticky ? 0 : LIFETIME[n.kind];
      if (life) timers.current.set(id, window.setTimeout(() => dismiss(id), life));
      // At night only a critical failure is allowed to make noise.
      if (silent) return;
      if (n.kind === "warn") return; // warnings are visual only; only failures and good news make a sound
      // Quiet hours silence the sound only; notices and vibration carry on.
      const now = Date.now();
      if (soundRef.current && !mutedRef.current && (!quietRef.current || n.kind === "crit") && now - lastTone.current >= MIN_TONE_GAP_MS) {
        lastTone.current = now;
        beeper.current.play(n.kind);
      }
      if (!quietRef.current || n.kind === "crit") buzz(HAPTIC[n.kind === "ok" ? "ok" : n.kind]);
    },
    [dismiss],
  );

  // Problems: announce one only after it has lasted a while; say "recovered"
  // only for problems that were announced. Short blips stay silent.
  useEffect(() => {
    problemsRef.current = problems;
  }, [problems]);
  useEffect(() => {
    const evaluate = () => {
      const list = problemsRef.current;
      if (!list) return;
      const now = Date.now();
      const current = new Map(list.map((p) => [`${p.type}:${p.name}`, p]));
      const first = !baselined.current;
      baselined.current = true;
      for (const [key, p] of current) {
        let s = seen.current.get(key);
        if (!s) {
          // what was already broken when the page opened counts as known
          s = { since: now, announced: first, p };
          seen.current.set(key, s);
        }
        s.p = p;
        const kind = p.severity === "crit" ? "crit" : "warn";
        if (!s.announced && now - s.since >= SETTLE_MS[kind]) {
          s.announced = true;
          push({ kind: COUNT_KIND(p), label: p.severity === "crit" ? "Critical failure" : "Warning", name: p.name, text: p.reason, sticky: p.severity === "crit", key });
        }
      }
      for (const [key, s] of seen.current) {
        if (current.has(key)) continue;
        seen.current.delete(key);
        if (!s.announced) continue; // a blip we never mentioned
        setNotices((cur) => cur.filter((x) => x.key !== key));
        // good news is only worth a tone when the failure itself was
        push({ kind: "ok", label: "Recovered", name: s.p.name }, s.p.severity !== "crit");
      }
    };
    evaluate();
    const timer = window.setInterval(evaluate, 5000);
    return () => window.clearInterval(timer);
  }, [push]);

  // The vacuum: a finished run, or an error.
  const vacuum = devices.find((d) => d.domain === "vacuum" && !d.kind);
  const vState = vacuum?.state ?? null;
  useEffect(() => {
    if (!vacuum || vState === null) return;
    const was = prevVacuum.current;
    prevVacuum.current = vState;
    if (was === null || was === vState) return;
    if (vState === "error") push({ kind: "crit", label: "Vacuum error", name: vacuum.name, sticky: true, key: "vacuum:error" });
    else if (was === "cleaning" && (vState === "returning" || vState === "docked")) push({ kind: "info", label: "Cleaning finished", name: vacuum.name });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reacting to a change in external (polled) data
    if (vState !== "error") setNotices((cur) => cur.filter((x) => x.key !== "vacuum:error"));
  }, [vState, vacuum, push]);

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => window.clearTimeout(t));
  }, []);

  // Any tap unlocks audio (browser rule); remember whether it worked.
  const unlock = useCallback(() => {
    if (!soundRef.current) return;
    beeper.current.unlock();
    setAudioReady(beeper.current.ready());
  }, []);

  const setSound = useCallback(
    (on: boolean) => {
      setSoundState(on);
      try {
        localStorage.setItem(SOUND_KEY, on ? "on" : "off");
      } catch {
        /* ignore */
      }
      if (on) {
        beeper.current.unlock();
        window.setTimeout(() => {
          setAudioReady(beeper.current.ready());
          beeper.current.play("info");
        }, 60);
      }
    },
    [],
  );

  return { notices, dismiss, sound, setSound, unlock, audioReady };
}
